/**
 * 에피소드 등록부 — **에피소드 하나를 게임·에디터에 붙이는 곳.** 새 에피소드는 여기에 한 줄을 더한다.
 *
 * 무엇을 어디서 불러오는지만 적는다(전부 동적 import — 방 하나는 한 에피소드만 쓴다):
 *   level       레벨 번들 `shared/level-<ep>.bundle.ts` (공간·조사물·잠금·라운드 …) — 모양은 `LevelBundle`
 *   place       배치 관계 원본 `shared/level-<ep>.place.ts` — 배치 에디터가 「관계에서 온 것」을 보여 줄 때
 *   clueIntent  「단서가 스토리상 어디 있어야 하나」 `shared/clue-intent-<ep>.ts` (tools/gen-clue-intent.ts 생성물)
 * 로비 카드(제목·소개·준비됨)는 `shared/episode-rules.ts` 의 `EPISODE_CARDS`, 서버 단서·대본은 `server/src/*-active.ts`.
 */

import { setLevel, type LevelBundle } from './level';
import { DEFAULT_EPISODE, type EpisodeId } from './episode-rules';
import type { ClueIntent } from './clue-intent-saholv7';

type PlaceModule = Pick<typeof import('./level-saholv7.place'), 'PLACE' | 'PLACE_PROPS'>;

export interface EpisodeSource {
  id: EpisodeId;
  title: string;
  level: () => Promise<LevelBundle>;
  place: () => Promise<PlaceModule>;
  clueIntent: () => Promise<Record<string, ClueIntent>>;
}

export const EPISODE_SOURCES: Record<EpisodeId, EpisodeSource> = {
  saholv7: {
    id: 'saholv7',
    title: '사흘',
    level: () => import('./level-saholv7.bundle'),
    place: () => import('./level-saholv7.place'),
    clueIntent: async () => (await import('./clue-intent-saholv7')).CLUE_INTENT,
  },
};

export function isEpisodeId(id: unknown): id is EpisodeId {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(EPISODE_SOURCES, id);
}

/** 모르는 id 면 기본 에피소드 */
export function episodeOr(id: unknown): EpisodeId {
  return isEpisodeId(id) ? id : DEFAULT_EPISODE;
}

/** **클라이언트 전용** — 배럴(`shared/level`)을 이 에피소드로 갈아 끼운다. 씬을 짓기 전에 부른다 */
export async function useEpisodeLevel(id: EpisodeId): Promise<LevelBundle> {
  const b = await EPISODE_SOURCES[id].level();
  setLevel(id, b);
  return b;
}
